import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';

import { GitHubConnectMethod } from '../enums';
import { GET_TICKETING_CONNECTIONS, ProviderType, TicketingAcquisitionMethod, TicketingProvider } from '../graphql';
import { TicketingConnectionAction, TicketingConnectionRow } from '../utils';
import { useGitHubAppInstallReturn } from './useGitHubAppInstallReturn';

type DeterministicProvider = { provider: TicketingProvider; providerType: ProviderType };

/**
 * Which connect method a row action means. A customer clicking "Migrate to GitHub App" or "Relink"
 * on a specific connection has already chosen how to connect by choosing the action, so the
 * slide-over opens on that method rather than asking again.
 *
 * Only GitHub has a choice to skip; for every other provider the slide goes straight to Merge.
 */
const connectMethodFor = (
  action: TicketingConnectionAction,
  connection: TicketingConnectionRow,
): GitHubConnectMethod | null => {
  if (connection.token.ticketingProvider !== TicketingProvider.GithubIssues) {
    return null;
  }

  switch (action) {
    case TicketingConnectionAction.MigrateToGitHubApp:
    case TicketingConnectionAction.ResumeInstall:
    case TicketingConnectionAction.Reconnect:
      return GitHubConnectMethod.GITHUB_APP;
    case TicketingConnectionAction.Relink:
      return connection.token.acquisitionMethod === TicketingAcquisitionMethod.MergeDev
        ? GitHubConnectMethod.MERGE_DEV
        : null;
    default:
      return null;
  }
};

/** Every action this hook can route into the connect slide-over. */
export const CONNECT_SLIDE_OVER_ACTIONS: ReadonlySet<TicketingConnectionAction> = new Set([
  TicketingConnectionAction.MigrateToGitHubApp,
  TicketingConnectionAction.Relink,
  TicketingConnectionAction.ResumeInstall,
  TicketingConnectionAction.Reconnect,
]);

/**
 * The connect slide-over as the Data Sources pages drive it, in one place because getting it right
 * is not obvious and both products need the same thing.
 *
 * A GitHub App install is a full-page redirect minted with the page that began it as its return
 * URL, so it comes back to whichever Data Sources page started it, carrying
 * `?connected_github_app=…`. Both halves of that matter: reopening the slide-over on GitHub is how
 * the customer sees the install land, and clearing the param on close is what stops the next
 * connect from that page silently skipping the method-choice step.
 */
export const useTicketingConnectModal = (): {
  connectModalState: { isOpen: boolean; showProvidersInTypes: Set<ProviderType> };
  initialProvider: DeterministicProvider | null;
  initialGitHubConnectMethod: GitHubConnectMethod | null;
  initialOrganizationSlug: string | null;
  openForTypes: (providerTypes: ProviderType[]) => void;
  openForConnectionAction: (action: TicketingConnectionAction, connection: TicketingConnectionRow) => void;
  close: () => void;
} => {
  const [connectModalState, setConnectModalState] = useState<{
    isOpen: boolean;
    showProvidersInTypes: Set<ProviderType>;
  }>({ isOpen: false, showProvidersInTypes: new Set() });
  const [connectionEntry, setConnectionEntry] = useState<{
    provider: TicketingProvider;
    method: GitHubConnectMethod | null;
    organization: string | null;
  } | null>(null);
  const apolloClient = useApolloClient();
  const { closeConnectModal, installStatus: githubAppInstallReturn } = useGitHubAppInstallReturn();

  useEffect(() => {
    if (githubAppInstallReturn) {
      setConnectModalState({ isOpen: true, showProvidersInTypes: new Set([ProviderType.Ticketing]) });
    }
  }, [githubAppInstallReturn]);

  // The provider and method seeds are read only on the closed → open edge, so calling this while the
  // slide-over is open leaves a part-way-through customer on their slide. `showProvidersInTypes` is
  // forwarded live rather than seeded, though, so it would change the picker's contents under them —
  // no caller can reach that today, since every open goes through the overlay.
  const openForTypes = useCallback((providerTypes: ProviderType[]): void => {
    setConnectionEntry(null);
    setConnectModalState({ isOpen: true, showProvidersInTypes: new Set(providerTypes) });
  }, []);

  const openForConnectionAction = useCallback(
    (action: TicketingConnectionAction, connection: TicketingConnectionRow): void => {
      setConnectionEntry({
        provider: connection.token.ticketingProvider,
        method: connectMethodFor(action, connection),
        // Every row action already names an organization — including a migration, where the Merge
        // connection being migrated from reads from the very org the App is to be installed on. The
        // install writes whatever is in that field onto the token, and a typo there can never be
        // correlated with the webhook, so a value we already hold is not one to ask for again. Only
        // a fresh add starts empty.
        organization: connection.organization,
      });
      setConnectModalState({ isOpen: true, showProvidersInTypes: new Set([ProviderType.Ticketing]) });
    },
    [],
  );

  const wasTicketingSession = connectModalState.showProvidersInTypes.has(ProviderType.Ticketing);

  /**
   * Consumes the per-query promises rather than leaving them dangling: a refetch that fails on a
   * flaky network would otherwise surface as an unhandled rejection. A stale row is what the next
   * page load fixes, and is not worth anything louder at close time.
   */
  const refreshConnections = useCallback(async (): Promise<void> => {
    const { results } = apolloClient.refetchQueries({ include: [GET_TICKETING_CONNECTIONS] });
    try {
      await Promise.all(results);
    } catch {
      // Nothing the customer can act on, and the close already happened.
    }
  }, [apolloClient]);

  const close = useCallback((): void => {
    setConnectModalState((state) => ({ ...state, isOpen: false }));
    closeConnectModal();

    // Relinking and installing land their results on the connections query's own fields —
    // needsRelink, installationStatus, lastSuccessfulSync — and nothing in those flows writes that
    // document, so without this a completed repair leaves the row still badged as broken. Only for
    // a ticketing session, though: on the RD page this same slide-over connects accounting and
    // payroll, and cancelling out of those changed nothing here.
    if (wasTicketingSession) {
      void refreshConnections();
    }
  }, [closeConnectModal, refreshConnections, wasTicketingSession]);

  return {
    connectModalState,
    initialProvider: connectionEntry
      ? { provider: connectionEntry.provider, providerType: ProviderType.Ticketing }
      : githubAppInstallReturn
        ? { provider: TicketingProvider.GithubIssues, providerType: ProviderType.Ticketing }
        : null,
    initialGitHubConnectMethod: connectionEntry?.method ?? null,
    initialOrganizationSlug: connectionEntry?.organization ?? null,
    openForTypes,
    openForConnectionAction,
    close,
  };
};
